import { log } from '@/lib/logger'
// Facebook OAuth Service
// Handles Facebook Login SDK initialization and OAuth flow

import { FACEBOOK_APP_ID, META_GRAPH_API_VERSION, META_INTEGRATION_ENABLED } from '@/config/env'

interface FacebookAuthResponse {
  accessToken?: string
  userID?: string
  expiresIn?: number
  code?: string
  phone_number_id?: string
  waba_id?: string
  [key: string]: unknown
}

interface FacebookLoginResponse {
  authResponse?: FacebookAuthResponse
  status: 'connected' | 'not_authorized' | 'unknown'
  code?: string
  phone_number_id?: string
  waba_id?: string
  [key: string]: unknown
}

declare global {
  interface Window {
    FB: {
      init: (params: {
        appId: string
        cookie: boolean
        xfbml: boolean
        version: string
      }) => void
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options?: {
          scope?: string
          return_scopes?: boolean
          auth_type?: string
          config_id?: string
          extras?: Record<string, unknown>
        }
      ) => void
      getLoginStatus: (
        callback: (response: FacebookLoginResponse) => void
      ) => void
    }
    fbAsyncInit: () => void
  }
}

export class FacebookOAuthError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FacebookOAuthError'
  }
}

const DEFAULT_META_LOGIN_SCOPES = [
  'public_profile',
  'email',
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'business_management',
  'whatsapp_business_management',
  'whatsapp_business_messaging',
  // Conversion events of WhatsApp chats (ConversionEventDialog). With Embedded
  // Signup the scopes come from the login configuration in the Meta dashboard
  'whatsapp_business_manage_events',
]

export const MESSENGER_LOGIN_SCOPES = [
  'public_profile',
  'email',
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'pages_messaging',
  // Page comments and their automatic reply (FacebookPage)
  'pages_read_user_content',
  'pages_manage_engagement',
  // Conversion events of Messenger chats, and of Instagram chats sent with this
  // Page token (ConversionEventDialog)
  'page_events',
  'instagram_manage_events',
  // Messenger outside the 24-hour window: utility templates and paid marketing
  // messages (messenger-outreach-actions, Facebook page tabs)
  'pages_utility_messaging',
  'marketing_messages_messenger',
  'paid_marketing_messages',
  // Product catalogs of the business (CatalogTab)
  'catalog_management',
  // Meta apps the user manages and their WhatsApp solutions (MetaAppsTab)
  'manage_app_solution',
  // pages_user_locale / _timezone / _gender are not login scopes: Meta grants
  // them to the app itself (App Review) and they apply to the Page token
  'business_management',
]

export const INSTAGRAM_LOGIN_SCOPES = [
  'public_profile',
  'email',
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'pages_messaging',
  'instagram_basic',
  'instagram_manage_messages',
  'business_management',
]

export const FACEBOOK_ADS_LEADS_LOGIN_SCOPES = [
  'public_profile',
  'email',
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_metadata',
  'pages_manage_ads',
  'ads_read',
  // Pause / resume a campaign (FacebookCampaignsList)
  'ads_management',
  'leads_retrieval',
  'business_management',
]

let sdkLoadPromise: Promise<void> | null = null

class FacebookOAuthService {
  private appId: string
  private sdkInitialized: boolean = false
  private initPromise: Promise<void> | null = null

  constructor() {
    this.appId = FACEBOOK_APP_ID
    if (META_INTEGRATION_ENABLED && this.appId) {
      this.initialize().catch((error) => {
        log.warn('Facebook SDK preload failed', {
          error: error instanceof Error ? error.message : String(error),
        })
      })
    } else if (META_INTEGRATION_ENABLED) {
      log.warn('Meta integration is enabled but VITE_FACEBOOK_APP_ID is not configured')
    }
  }

  async initialize(): Promise<void> {
    return this.initSDK()
  }

  isInitialized(): boolean {
    return typeof window !== 'undefined' && Boolean(window.FB && this.sdkInitialized)
  }

  /**
   * Initialize Facebook SDK
   * Waits for SDK to load and then initializes it
   */
  private async initSDK(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      log.debug('Initializing Facebook SDK...', { appId: this.appId })

      // If SDK is already loaded and we already called FB.init, we're done
      if (window.FB && this.sdkInitialized) {
        log.debug('Facebook SDK already initialized')
        resolve()
        return
      }

      const doInit = () => {
        try {
          this.initializeFB()
          resolve()
        } catch (error) {
          console.error('Error initializing Facebook SDK:', error)
          reject(error)
        }
      }

      this.loadSDK()
        .then(doInit)
        .catch(reject)
    })

    return this.initPromise
  }

  private loadSDK(): Promise<void> {
    if (window.FB) {
      return Promise.resolve()
    }

    if (sdkLoadPromise) {
      return sdkLoadPromise
    }

    sdkLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById('facebook-jssdk') as HTMLScriptElement | null

      window.fbAsyncInit = () => {
        log.debug('Facebook SDK fbAsyncInit called')
        resolve()
      }

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true })
        existingScript.addEventListener('error', () => reject(new FacebookOAuthError('Facebook SDK failed to load')), { once: true })
        return
      }

      const script = document.createElement('script')
      script.id = 'facebook-jssdk'
      script.async = true
      script.defer = true
      script.crossOrigin = 'anonymous'
      script.src = 'https://connect.facebook.net/en_US/sdk.js'
      script.onerror = () => reject(new FacebookOAuthError('Facebook SDK failed to load'))
      document.body.appendChild(script)

      setTimeout(() => {
        if (!window.FB) {
          reject(new FacebookOAuthError(
            'Facebook SDK failed to load. Please check your internet connection and try again.'
          ))
        }
      }, 10000)
    })

    return sdkLoadPromise
  }

  /**
   * Initialize Facebook SDK with app ID
   */
  private initializeFB(): void {
    if (this.sdkInitialized) {
      log.debug('Facebook SDK already initialized, skipping')
      return
    }

    if (!window.FB) {
      throw new FacebookOAuthError('Facebook SDK (window.FB) is not available')
    }

    if (!this.appId) {
      throw new FacebookOAuthError('Facebook App ID is not configured')
    }

    try {
      log.debug(`Calling FB.init with App ID: ${this.appId}, version: ${META_GRAPH_API_VERSION}`)
      window.FB.init({
        appId: this.appId,
        cookie: true,
        xfbml: true,
        version: META_GRAPH_API_VERSION,
      })
      this.sdkInitialized = true
      log.debug('Facebook SDK initialized successfully')
    } catch (error) {
      console.error('Error in FB.init:', error)
      throw new FacebookOAuthError(`Failed to initialize Facebook SDK: ${error}`)
    }
  }

  /**
   * Ensure FB.init() has been called before using FB.login() or other methods.
   * Re-calls FB.init() if needed (safe to call multiple times).
   */
  private ensureInitialized(): void {
    if (!window.FB) {
      throw new FacebookOAuthError('Facebook SDK (window.FB) is not available')
    }
    if (!this.appId) {
      throw new FacebookOAuthError('Facebook App ID is not configured')
    }
    // FB.init() is idempotent — calling it again is harmless and ensures the
    // SDK's internal state is correct even if the first call raced with loading.
    window.FB.init({
      appId: this.appId,
      cookie: true,
      xfbml: true,
      version: META_GRAPH_API_VERSION,
    })
    this.sdkInitialized = true
  }

  /**
   * Login with Facebook OAuth
   * Opens popup and requests permissions
   * @param configurationId - Optional configuration ID from Meta App Dashboard to use a specific template
   * @param responseType - Optional response type ('token' or 'code'), defaults to 'token' unless config_id is present
   */
  async login(configurationId?: string, responseType?: 'token' | 'code', scopes: string[] = DEFAULT_META_LOGIN_SCOPES): Promise<string> {
    if (!META_INTEGRATION_ENABLED) {
      throw new FacebookOAuthError('Meta integration is disabled in this environment')
    }

    log.debug('Starting Facebook login...', { configurationId, responseType })

    if (!this.isInitialized()) {
      try {
        await this.initSDK()
      } catch (error) {
        console.error('SDK initialization failed:', error)
        throw error
      }
    }

    if (!this.sdkInitialized || !window.FB) {
      throw new FacebookOAuthError('Facebook SDK not initialized')
    }

    // Re-call FB.init() right before FB.login() to guard against race conditions
    // where the SDK loaded but init didn't stick (produces "init not called with valid version")
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      let completed = false
      const completeResolve = (value: string) => {
        if (completed) {
          return
        }
        completed = true
        window.clearTimeout(loginTimeout)
        resolve(value)
      }
      const completeReject = (error: FacebookOAuthError) => {
        if (completed) {
          return
        }
        completed = true
        window.clearTimeout(loginTimeout)
        reject(error)
      }
      const loginTimeout = window.setTimeout(() => {
        completeReject(new FacebookOAuthError(
          'Facebook login did not complete. Please allow popups for this site and try again.'
        ))
      }, 45000)

      const requestedScopes = Array.from(new Set(scopes)).join(',')

      // Determine response type
      // If configurationId is provided, we likely need 'code' flow for business login
      // But let's allow explicit override
      const actualResponseType = responseType || (configurationId ? 'code' : 'token')

      const loginOptions: {
        scope: string
        return_scopes: boolean
        auth_type: string
        config_id?: string
        response_type?: string
        override_default_response_type?: boolean
      } = {
        scope: requestedScopes,
        return_scopes: true,
        auth_type: 'rerequest',
        response_type: actualResponseType,
        override_default_response_type: actualResponseType === 'code',
      }

      if (configurationId) {
        loginOptions.config_id = configurationId
        log.debug('Using configuration ID:', configurationId)
      }

      log.debug('Calling FB.login with options:', loginOptions)

      window.FB.login(
        (response) => {
          log.debug('FB.login response:', response)
          log.debug('FB.login response details:', {
            status: response.status,
            authResponse: response.authResponse,
            code: response.code || response.authResponse?.code,
            fullResponse: JSON.stringify(response, null, 2)
          })
          if (response.status === 'connected') {
            // Handle token flow
            if (response.authResponse?.accessToken) {
              log.debug('Login successful, access token received')
              completeResolve(response.authResponse.accessToken)
              return
            }

            // Handle code flow (response might be different structure or in authResponse)
            // The type definition for authResponse might need update if it contains 'code'
            // For now, check if 'code' exists in response or authResponse
            const code = response.authResponse?.code || response.code

            if (code) {
              log.debug('Login successful, authorization code received')
              completeResolve(code)
              return
            }

            // Fallback if connected but no token/code found
            console.warn('Login status connected but no token or code found', response)
            completeReject(new FacebookOAuthError('Login successful but no token or code received'))

          } else if (response.status === 'not_authorized') {
            completeReject(new FacebookOAuthError('Facebook did not grant the required Instagram permissions. Please retry and approve all requested permissions.'))
          } else if (response.status === 'unknown') {
            // Status 'unknown' can mean:
            // - User cancelled the login popup
            // - Popup was blocked by browser
            // - Login flow didn't complete
            // - For Business Login with config_id, code might still be present
            console.warn('Facebook login returned unknown status:', response)
            
            // Check if code exists in response (for Business Login configurations)
            // The code might be in authResponse.code, response.code, or nested deeper
            let code = response.authResponse?.code || response.code
            
            // Also check if authResponse exists and has any code-like property
            if (!code && response.authResponse) {
              const authResp = response.authResponse
              code = (authResp.code || authResp.authorizationCode || authResp.authCode) as string | undefined
            }
            
            if (code && configurationId) {
              // For Business Login, code might be present even with unknown status
              log.debug('Code found in unknown status response (Business Login):', code)
              completeResolve(code)
              return
            }

            window.FB.getLoginStatus((statusResponse) => {
              log.debug('FB.getLoginStatus fallback response:', statusResponse)

              if (statusResponse.status === 'connected' && statusResponse.authResponse?.accessToken) {
                log.debug('Recovered access token from FB.getLoginStatus fallback')
                completeResolve(statusResponse.authResponse.accessToken)
                return
              }
            
              // Log full response for debugging
              console.error('No code found in unknown status response. Full response:', JSON.stringify(response, null, 2))
              completeReject(new FacebookOAuthError('Instagram login did not complete. If a popup opened, confirm it and try again.'))
            })
            return
          } else {
            completeReject(new FacebookOAuthError('User cancelled login or login failed'))
          }
        },
        loginOptions
      )
    })
  }

  /**
   * Get current login status
   */
  async getLoginStatus(): Promise<{
    status: 'connected' | 'not_authorized' | 'unknown'
    accessToken?: string
  }> {
    if (!META_INTEGRATION_ENABLED) {
      throw new FacebookOAuthError('Meta integration is disabled in this environment')
    }

    await this.initSDK()

    if (!this.sdkInitialized) {
      throw new FacebookOAuthError('Facebook SDK not initialized')
    }

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response) => {
        resolve({
          status: response.status,
          accessToken: response.authResponse?.accessToken,
        })
      })
    })
  }

  /**
   * WhatsApp Embedded Signup Login
   * Returns the full response including WABA ID and phone number ID
   * Uses sessionInfoListener callback to capture embedded signup data
   */
  async whatsappEmbeddedSignup(configurationId: string): Promise<{
    code: string
    phone_number_id?: string
    waba_id?: string
  }> {
    if (!META_INTEGRATION_ENABLED) {
      throw new FacebookOAuthError('Meta integration is disabled in this environment')
    }

    log.debug('Starting WhatsApp Embedded Signup...', { configurationId })

    try {
      await this.initSDK()
    } catch (error) {
      console.error('SDK initialization failed:', error)
      throw error
    }

    if (!this.sdkInitialized || !window.FB) {
      throw new FacebookOAuthError('Facebook SDK not initialized')
    }

    // Re-call FB.init() right before FB.login() to guard against race conditions
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      let capturedPhoneNumberId: string | undefined
      let capturedWabaId: string | undefined

      const sessionInfoListener = (sessionInfo: { phone_number_id?: string; phoneNumberId?: string; waba_id?: string; wabaId?: string; whatsapp_business_account_id?: string; data?: { phone_number_id?: string; waba_id?: string }; [key: string]: unknown }) => {
        log.debug('WhatsApp Embedded Signup sessionInfoListener callback:', sessionInfo)
        log.debug('sessionInfo full data:', JSON.stringify(sessionInfo, null, 2))
        
        if (sessionInfo) {
          capturedPhoneNumberId = sessionInfo.phone_number_id || 
                                   sessionInfo.phoneNumberId ||
                                   sessionInfo.data?.phone_number_id
          capturedWabaId = sessionInfo.waba_id || 
                          sessionInfo.wabaId ||
                          sessionInfo.whatsapp_business_account_id ||
                          sessionInfo.data?.waba_id
          
          log.debug('Captured from sessionInfoListener:', {
            phone_number_id: capturedPhoneNumberId,
            waba_id: capturedWabaId,
          })
        }
      }

      // Per Meta docs: when using config_id, scopes are defined in the
      // Facebook Login configuration on the dashboard, not here.
      const loginOptions = {
        config_id: configurationId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: '3',
          sessionInfoListener: sessionInfoListener,
        }
      }

      log.debug('Calling FB.login for WhatsApp Embedded Signup with options:', loginOptions)

      window.FB.login(
        (response: FacebookLoginResponse) => {
          log.debug('WhatsApp Embedded Signup response:', response)
          log.debug('WhatsApp Embedded Signup full response:', JSON.stringify(response, null, 2))
          
          // Log all properties for debugging
          if (response.authResponse) {
            log.debug('authResponse keys:', Object.keys(response.authResponse))
            log.debug('authResponse:', JSON.stringify(response.authResponse, null, 2))
          }
          
          if (response.status === 'connected' || response.authResponse) {
            const authResp = (response.authResponse ?? {}) as FacebookAuthResponse
            
            // Extract code
            const code = authResp.code || response.code
            if (!code) {
              console.error('No code found in WhatsApp Embedded Signup response')
              reject(new FacebookOAuthError('No authorization code received from WhatsApp Embedded Signup'))
              return
            }
            
            // Try to get WhatsApp-specific fields from multiple sources:
            // 1. From sessionInfoListener callback (captured earlier)
            // 2. From authResponse directly
            // 3. From response top-level
            const phone_number_id = capturedPhoneNumberId || 
                                    authResp.phone_number_id || 
                                    response.phone_number_id
            const waba_id = capturedWabaId || 
                           authResp.waba_id || 
                           response.waba_id
            
            log.debug('WhatsApp Embedded Signup extracted data:', {
              code_length: code.length,
              phone_number_id,
              waba_id,
              source: capturedPhoneNumberId ? 'sessionInfoListener' : 'authResponse/response',
            })
            
            resolve({
              code,
              phone_number_id,
              waba_id,
            })
            return
          }
          
          if (response.status === 'unknown') {
            const authResp = response.authResponse as FacebookAuthResponse | undefined
            // Check if code exists despite unknown status
            const code = authResp?.code || response.code
            if (code) {
              const phone_number_id = capturedPhoneNumberId || 
                                      authResp?.phone_number_id || 
                                      response.phone_number_id
              const waba_id = capturedWabaId || 
                             authResp?.waba_id || 
                             response.waba_id
              
              log.debug('Code found in unknown status (WhatsApp Embedded Signup):', {
                code_length: code.length,
                phone_number_id,
                waba_id,
              })
              
              resolve({
                code,
                phone_number_id,
                waba_id,
              })
              return
            }
          }
          
          console.error('WhatsApp Embedded Signup failed:', response)
          reject(new FacebookOAuthError('WhatsApp Embedded Signup was cancelled or failed. Please try again.'))
        },
        loginOptions
      )
    })
  }
}

// Export singleton instance
export const facebookOAuthService = new FacebookOAuthService()
